import React from 'react';
import cx from 'classnames';
import {observer} from 'mobx-react';
import {isRTL} from '../../../../Localization';
import {PAGE_LIMIT} from '../../consts/pagination';
import {Form} from '../../../../stores/Form/Form';
import {FormViewEnum} from '../../../../stores/Form/Form';
import {recordViewPaginationGoTo, requestPaginationData} from '../../actions/pagination';
import texts from '../../texts';
import {getTotal} from './usePaginationData';
import {Pagination} from './Pagination';
import styles from './styles.module.scss';

type Props = {
    form: Form;
    componentClasses?: string;
    formView: FormViewEnum;
    isActiveForm: boolean;
};

export const FormPagination = observer((props: Props) => {
    const {isActiveForm, form, formView, componentClasses} = props;
    const {
        paginationOffset,
        currentRow: rowIndex,
        records: tableRecords,
        hasMore,
        currentRecord,
        allRecordsCount
    } = form;
    const filledRecords = tableRecords.filter(r => !r.isNew);

    const pageRangeStart = paginationOffset;
    const currentRowPosition = pageRangeStart + rowIndex + 1;

    const pageRangeEnd =
        filledRecords.length < PAGE_LIMIT
            ? Math.min(paginationOffset + filledRecords.length, allRecordsCount)
            : Math.min(paginationOffset + PAGE_LIMIT, allRecordsCount);

    if ((filledRecords.length === 0 && allRecordsCount === 0) || form.searchModeLevel) {
        return null;
    }

    /*
     *  Record view pagination
     **/

    if (formView === FormViewEnum.form) {
        const onPrevRecordClick = async () => {
            // if current row is  the first row of the set we should fetch a new set of pagination data,
            const shouldFetchNewData = rowIndex === 0;
            const newFocusRowIndex = shouldFetchNewData ? PAGE_LIMIT - 1 : rowIndex - 1;
            const offset = shouldFetchNewData
                ? Math.max(0, pageRangeStart - PAGE_LIMIT)
                : undefined;

            await recordViewPaginationGoTo(form, newFocusRowIndex, shouldFetchNewData, offset);
        };

        const onNextRecordClick = async () => {
            const shouldFetchNewData = rowIndex >= filledRecords.length - 1 && hasMore;
            const newFocusRowIndex = shouldFetchNewData ? 0 : rowIndex + 1;
            const offset = shouldFetchNewData ? pageRangeEnd : undefined;

            await recordViewPaginationGoTo(form, newFocusRowIndex, shouldFetchNewData, offset);
        };

        const totalRows = getTotal(form).toString();

        const recordViewDisabledNext = () => {
            if (!isActiveForm) return true;
            const maxFilledRowsCount = paginationOffset + filledRecords.length;

            if (currentRecord.isNew) {
                // it response for the case when we inject `temporarily` empty records
                return currentRowPosition >= maxFilledRowsCount + 1 && !hasMore;
            }

            return currentRowPosition >= maxFilledRowsCount && !hasMore;
        };

        return (
            <Pagination
                form={form}
                current={currentRowPosition.toString()}
                total={totalRows}
                onPrevClick={onPrevRecordClick}
                onNextClick={onNextRecordClick}
                disablePrev={!isActiveForm || currentRowPosition === 1}
                disableNext={recordViewDisabledNext()}
                nextBtnTooltipText={texts.next_record_tooltip}
                prevBtnTooltipText={texts.prev_record_tooltip}
            />
        );
    }

    /*
     *  Table view pagination
     **/

    const onPrevClick = async () => {
        if (paginationOffset > 1) {
            const offset = pageRangeStart - PAGE_LIMIT;
            await requestPaginationData(form, PAGE_LIMIT, Math.max(0, offset));
        }
    };

    const onNextClick = async () => {
        if (hasMore) {
            await requestPaginationData(form, PAGE_LIMIT, pageRangeEnd);
        }
    };

    const endRangeGrid = currentRecord?.isNew ? pageRangeEnd + 1 : pageRangeEnd;

    const currentRange = isRTL()
        ? `${endRangeGrid} - ${pageRangeStart + 1}`
        : `${pageRangeStart + 1} - ${endRangeGrid}`;

    const totalRows = getTotal(form).toString();

    return (
        <div className={cx(styles.component, componentClasses)}>
            <Pagination
                form={form}
                current={currentRange}
                total={totalRows}
                onPrevClick={onPrevClick}
                onNextClick={onNextClick}
                disablePrev={!isActiveForm || paginationOffset < PAGE_LIMIT}
                disableNext={!isActiveForm || !hasMore}
                nextBtnTooltipText={texts.next_page_tooltip}
                prevBtnTooltipText={texts.prev_page_tooltip}
            />
        </div>
    );
});
